/**
 * PostHog Browser Initialization
 *
 * Autocapture + heatmaps + SPA-safe pageviews. Named events on top of
 * the autocapture baseline are emitted via `lib/analytics.ts`.
 *
 * Lazy-loaded:
 *
 *   `posthog-js` ships ~50 KB minified — small individually, but
 *   it's loaded into the boot bundle even though autocapture doesn't
 *   need to fire in the first few hundred ms of a session. Switching
 *   to a dynamic import keeps the package out of the main chunk and
 *   defers init until the first analytics call (or login, whichever
 *   comes first). Every call site stays fire-and-forget; missing
 *   events during the load window are an acceptable trade.
 *
 *   In non-prod and when `POSTHOG_TOKEN` is unset, `getPosthog()`
 *   returns `undefined` synchronously so call sites short-circuit
 *   without a microtask.
 */
const POSTHOG_TOKEN = process.env.POSTHOG_TOKEN;
const POSTHOG_HOST = process.env.POSTHOG_HOST ?? "https://us.i.posthog.com";
let posthogPromise;
/**
 * Returns a promise for the initialized PostHog client, or `undefined`
 * in environments where PostHog should be disabled (dev, missing
 * token). First call kicks off `import("posthog-js")` and runs
 * `init`; subsequent calls join the same promise.
 *
 * Fire-and-forget call sites should `void getPosthog()?.then(...)`.
 * Sync `if (!ph) return;` shortcuts can use `isPosthogEnabled()`.
 */
export function getPosthog() {
    if (!isPosthogEnabled())
        return undefined;
    if (!posthogPromise) {
        posthogPromise = import(
        /* webpackChunkName: "posthog" */ "posthog-js").then(({ default: ph }) => {
            ph.init(POSTHOG_TOKEN, {
                api_host: POSTHOG_HOST,
                defaults: "2026-01-30",
                capture_pageview: "history_change",
                capture_pageleave: true,
                autocapture: true,
                enable_heatmaps: true,
            });
            return ph;
        });
    }
    return posthogPromise;
}
/**
 * Synchronous check for "should PostHog be loaded at all?" — useful
 * for call sites that want to bail out before the dynamic import
 * even starts (e.g. dev console-mirror paths in `analytics.ts`).
 */
export function isPosthogEnabled() {
    return Boolean(POSTHOG_TOKEN) && process.env.NODE_ENV === "production";
}
/**
 * Identify the current user. Fire-and-forget; queued behind the
 * dynamic import if `posthog-js` hasn't loaded yet.
 */
export function identifyPosthog(userId) {
    void getPosthog()?.then((ph) => ph.identify(userId));
}
/** Reset PostHog's per-session state. Called on logout. */
export function resetPosthog() {
    void getPosthog()?.then((ph) => ph.reset());
}
